import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Text from "../../../components/atoms/Text";
import Button from "../../../components/atoms/Button";
import { DateRangePicker } from "../../../components/molecules/DateRangePicker";
import { getDateStringFromToday } from "../../../utils/TimeUtil";
import { AdminFeedbackTable } from "../../../components/templates/AdminFeedbackTable";
import { fetchAdminFeedbackList } from "../../../api/aiReviewService";
import { ToastManager } from "../../../components/organisms/CommonUI/ToastPopup/ToastManager";
import { WaitingManager } from "../../../components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { AIReviewFeedback } from "common/models/types";
import DownloadUtil from "../../../utils/DownloadUtil";
import { exportFeedbacksToCSV, getFormattedDate } from "../../../utils/csvExportUtil";

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled(Text)`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  width: 100%;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled(Text)`
  min-width: 80px;
  font-weight: 500;
  color: #666;
`;

const DateFilterWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
`;

const QuickDateButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#ddd")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#666")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const CSVButton = styled(Button)`
  margin-left: auto;
  min-width: 140px;
`;

const AdminFeedbacksPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState<AIReviewFeedback[]>([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState<AIReviewFeedback[]>([]);

  // 필터 상태
  const [startDate, setStartDate] = useState(getDateStringFromToday({ days: -7 })); // 기본 1주일
  const [endDate, setEndDate] = useState(getDateStringFromToday());
  const [quickDateRange, setQuickDateRange] = useState("1week");
  const [searchKeyword, setSearchKeyword] = useState("");

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // 날짜 빠른 선택
  const handleQuickDateSelect = (range: string) => {
    setQuickDateRange(range);
    const today = getDateStringFromToday();

    switch (range) {
      case "today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "1week":
        setStartDate(getDateStringFromToday({ days: -7 }));
        setEndDate(today);
        break;
      case "1month":
        setStartDate(getDateStringFromToday({ days: -30 }));
        setEndDate(today);
        break;
      case "3months":
        setStartDate(getDateStringFromToday({ days: -90 }));
        setEndDate(today);
        break;
    }
  };

  // 날짜 변경 핸들러
  const handleDateChange = (name: "start" | "end", date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    if (name === "start") {
      setStartDate(dateString);
    } else {
      setEndDate(dateString);
    }
    setQuickDateRange(""); // 직접 선택 시 빠른 선택 해제
  };

  // 데이터 로드
  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      WaitingManager.show({ message: "피드백 목록을 불러오는 중..." });

      const response = await fetchAdminFeedbackList({
        startDate,
        endDate,
      });

      setFeedbacks(response.data || []);
    } catch (error) {
      console.error("Failed to load feedbacks:", error);
      ToastManager.show({ content: "피드백 목록을 불러오는데 실패했습니다." });
    } finally {
      setLoading(false);
      WaitingManager.hide();
    }
  };

  // 검색 필터링
  const filterFeedbacks = () => {
    if (!searchKeyword.trim()) {
      setFilteredFeedbacks(feedbacks);
      return;
    }

    const keyword = searchKeyword.toLowerCase();
    const filtered = feedbacks.filter((feedback) => {
      const aiReview = feedback.aiReview;
      const user = feedback.user; // 사용자 정보는 feedback.user에 있음
      const patientId = (aiReview as any)?.request?.patientId || aiReview?.patientId;
      return (
        feedback.id?.toString().includes(keyword) ||
        feedback.aiReviewId?.toString().includes(keyword) ||
        user?.email?.toLowerCase().includes(keyword) ||
        user?.name?.toLowerCase().includes(keyword) ||
        patientId?.toString().toLowerCase().includes(keyword) ||
        feedback.comment?.toLowerCase().includes(keyword)
      );
    });

    setFilteredFeedbacks(filtered);
  };

  // 페이지별 데이터
  const paginatedFeedbacks = filteredFeedbacks.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // 상세보기 팝업
  const handleViewDetail = (reviewId: number) => {
    // 화면 크기의 90%를 차지하도록 설정, 최대 1920x1080
    const width = Math.min(1920, window.screen.width * 0.9);
    const height = Math.min(1080, window.screen.height * 0.9);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/admin/ai-reviews/${reviewId}/popup`,
      `ai-review-${reviewId}`,
      `width=${width},height=${height},left=${left},top=${top},` +
        `toolbar=no,location=no,directories=no,status=no,menubar=no,` +
        `scrollbars=yes,resizable=yes`
    );
  };

  // CSV 다운로드 핸들러
  const handleDownloadCSV = () => {
    try {
      if (filteredFeedbacks.length === 0) {
        ToastManager.show({ content: "다운로드할 데이터가 없습니다." });
        return;
      }

      // CSV 데이터 생성
      const csvData = exportFeedbacksToCSV(filteredFeedbacks);

      // 파일명 생성
      const filename = `AI_리뷰_피드백_${getFormattedDate()}.csv`;

      // 다운로드 실행
      DownloadUtil.downloadCsv(csvData, filename);

      ToastManager.show({
        content: `${filteredFeedbacks.length}건의 데이터를 다운로드했습니다.`,
      });
    } catch (error) {
      console.error("CSV 다운로드 실패:", error);
      ToastManager.show({ content: "CSV 다운로드에 실패했습니다." });
    }
  };

  // 초기 로드 및 날짜 변경 시 재로드
  useEffect(() => {
    loadFeedbacks();
  }, [startDate, endDate]);

  // 검색어 변경 시 필터링
  useEffect(() => {
    filterFeedbacks();
    setCurrentPage(1); // 검색 시 첫 페이지로
  }, [searchKeyword, feedbacks]);

  return (
    <Container>
      <Header>
        <Title>AI 리뷰 피드백 관리</Title>
      </Header>

      <FilterSection>
        <FilterRow>
          <FilterLabel>기간 선택</FilterLabel>
          <DateFilterWrapper>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChangeDate={handleDateChange}
              disabled={loading}
            />
            <QuickDateButton
              active={quickDateRange === "today"}
              onClick={() => handleQuickDateSelect("today")}
            >
              오늘
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1week"}
              onClick={() => handleQuickDateSelect("1week")}
            >
              1주일
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "1month"}
              onClick={() => handleQuickDateSelect("1month")}
            >
              1개월
            </QuickDateButton>
            <QuickDateButton
              active={quickDateRange === "3months"}
              onClick={() => handleQuickDateSelect("3months")}
            >
              3개월
            </QuickDateButton>
            <CSVButton
              size="Medium"
              type="Text"
              variant="Contained"
              color="Primary"
              onClick={handleDownloadCSV}
              disabled={loading || filteredFeedbacks.length === 0}
            >
              CSV 다운로드 ({filteredFeedbacks.length}건)
            </CSVButton>
          </DateFilterWrapper>
        </FilterRow>

        <FilterRow>
          <FilterLabel>검색</FilterLabel>
          <SearchInput
            type="text"
            placeholder="피드백 번호, 리뷰 번호, 사용자 이름, 이메일, 환자번호, 코멘트로 검색..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </FilterRow>
      </FilterSection>

      <AdminFeedbackTable
        feedbacks={paginatedFeedbacks}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        total={filteredFeedbacks.length}
        onPageChange={setCurrentPage}
        onViewDetail={handleViewDetail}
      />
    </Container>
  );
};

export default AdminFeedbacksPage;
